import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  JaarrekeningVenW
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  shareReplay,
  Subscription,
  switchMap, take,
} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type JaarrekeningVenWFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
  kostensoort: FormControl<string | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-resultaat-uit-overige-werkzaamheden',
  templateUrl: './resultaat-uit-overige-werkzaamheden.component.html',
  styleUrls: ['./resultaat-uit-overige-werkzaamheden.component.scss'],
  imports: [
    SharedUiDetailsModule,
    ReactiveFormsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    CommonModule
  ]
})
export class ResultaatUitOverigeWerkzaamhedenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public isPartner = false;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public formGroup = new FormGroup({
    rowOpbrengsten: new FormArray<JaarrekeningVenWFormGroup>([]),
    rowKosten: new FormArray<JaarrekeningVenWFormGroup>([]),
  });

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(partner => {
        this.isPartner = partner;
        return this.incomeTaxEntity$.pipe(
          take(1),
          map(entity => ({ entity, partner })));
      })
    ).subscribe(({ entity, partner }) => {
      this.handleSwitch(entity, partner);
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const toBedragen = (input: ReturnType<typeof this.extractInputForCalculation>) =>
      [...input.opbrengsten, ...input.kosten].map(row => row.bedrag ?? null);

    this.changeSubscriptions.add(
      merge(
        this.formGroup.controls.rowOpbrengsten.valueChanges,
        this.formGroup.controls.rowKosten.valueChanges
      ).pipe(
        map(() => this.saveForm()),
        map(() => this.extractInputForCalculation()),
        map(toBedragen),
        distinctUntilChanged((prev, curr) =>
          prev.length === curr.length && prev.every((v, i) => v === curr[i])
        ),
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((curr) => {
        if (curr.some(b => b !== null)) {
          this.dataService.calculateLatestIncomeTaxReturn().subscribe();
        }
      })
    );
  }

  private extractInputForCalculation(): {
    opbrengsten: { bedrag: number | null }[];
    kosten: { bedrag: number | null }[];
  } {
    return {
      opbrengsten: this.rowOpbrengsten.getRawValue().map(item => ({
        bedrag: item.bedrag,
      })),
      kosten: this.rowKosten.getRawValue().map(item => ({
        bedrag: item.bedrag,
      })),
    };
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    const data = details.box1?.resultaatUitOverigeWerkzaamheden;
    if (data) {
      this.updateForm(data?.rowOpbrengsten ?? [], this.rowOpbrengsten);
      this.updateForm(data?.rowKosten ?? [], this.rowKosten);
    }
    else {
      this.formGroup.reset({}, {emitEvent: false});
    }
  }

  get rowOpbrengsten(): FormArray {
    return this.formGroup.get('rowOpbrengsten') as FormArray;
  }

  get rowKosten(): FormArray {
    return this.formGroup.get('rowKosten') as FormArray;
  }

  addRijOpbrengsten(): void {
    this.rowOpbrengsten.push(this.createFormGroup(), { emitEvent: false })
  }

  addRijKosten(): void {
    this.rowKosten.push(this.createFormGroup(), { emitEvent: false })
  }

  verwijderRijRowOpbrengsten(index: number): void {
    this.rowOpbrengsten.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false })
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  verwijderRijRowKosten(index: number): void {
    this.rowKosten.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false })
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  createFormGroup(item?: JaarrekeningVenW): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving?.trim() ?? null, [
        Validators.required,
      ]),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null, [
        Validators.required,
        Validators.min(0)
      ]),
      kostensoort: new FormControl<string | null>(item?.kostensoort ?? null),
    })
  }

  private updateForm(list: JaarrekeningVenW[], array: FormArray): void {
    const lijst = list|| [];
    const formArray = array;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], { emitEvent: false });
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, { emitEvent: false });
      }
    }
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});
    const rawValue = this.formGroup.getRawValue();

    const filterEmptyRows = (rows: { omschrijving: string | null, bedrag: number | null }[]) =>
      rows.filter(row => row.omschrijving?.trim() || row.bedrag !== null);

    const update = (aangifte: Aangifte) => {
      const rowOpbrengsten = filterEmptyRows(rawValue.rowOpbrengsten);
      const rowKosten = filterEmptyRows(rawValue.rowKosten);

      return {
        ...aangifte,
        box1: {
          ...aangifte.box1,
          resultaatUitOverigeWerkzaamheden: {
            ...aangifte.box1?.resultaatUitOverigeWerkzaamheden,
            rowOpbrengsten: rowOpbrengsten.length ? rowOpbrengsten : undefined,
            rowKosten: rowKosten.length ? rowKosten : undefined,
          },
        },
      };
    };

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) return currentEntity;

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return { ...currentEntity, aangifte: updatedAangifte } as IncomeTaxEntity;
    });
  }
}
